import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { importNewsletterMissingRecipients } from '../../lib/materializeNewsletter';
import { tierLabel, type ProfileScoreTier } from '../../lib/personalization';
import { supabase } from '../../lib/supabase';

interface SubscriberOption {
  id: string;
  email: string | null;
  name: string | null;
  unsubscribed_at: string | null;
  profile_score: number | null;
  profile_score_tier: ProfileScoreTier | null;
}

interface AddNewsletterRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  newsletterId: string;
  newsletterName: string;
  onSuccess: () => void;
}

function displayLabel(s: SubscriberOption): string {
  return s.email?.trim() || s.name?.trim() || `subscriber ${s.id}`;
}

function profileScoreLabel(s: SubscriberOption): string {
  if (s.profile_score == null) return '—';
  const tier = s.profile_score_tier ? tierLabel(s.profile_score_tier) : '';
  return tier ? `${s.profile_score} · ${tier}` : String(s.profile_score);
}

function friendlyAddError(raw: string): string {
  if (raw.includes('subscriber_already_on_list')) {
    return 'This connection is already on the recipient list.';
  }
  if (raw.includes('subscriber_unsubscribed')) {
    return 'This connection is unsubscribed.';
  }
  if (raw.includes('subscriber_not_found')) {
    return 'Connection not found.';
  }
  if (raw.includes('body_html_required')) {
    return 'Could not render newsletter content. Check content blocks and try again.';
  }
  return raw;
}

export default function AddNewsletterRecipientModal({
  isOpen,
  onClose,
  newsletterId,
  newsletterName,
  onSuccess,
}: AddNewsletterRecipientModalProps) {
  const [subscribers, setSubscribers] = useState<SubscriberOption[]>([]);
  const [excludeIds, setExcludeIds] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const loadExcludeIds = useCallback(async () => {
    const { data, error } = await supabase
      .from('scheduled_emails')
      .select('subscriber_id')
      .eq('newsletter_id', newsletterId);
    if (error) throw error;
    setExcludeIds(new Set((data ?? []).map((r) => String(r.subscriber_id)).filter(Boolean)));
  }, [newsletterId]);

  const loadSubscribers = useCallback(async (query: string, background = false) => {
    if (background) {
      setIsSearching(true);
    } else {
      setLoading(true);
    }
    try {
      let req = supabase
        .from('subscribers')
        .select('id, email, name, unsubscribed_at, profile_score, profile_score_tier')
        .is('unsubscribed_at', null)
        .order('email', { ascending: true })
        .limit(100);

      const q = query.trim();
      if (q.length >= 2) {
        const pattern = `%${q.replace(/%/g, '\\%')}%`;
        req = req.or(`email.ilike.${pattern},name.ilike.${pattern}`);
      }

      const { data, error } = await req;
      if (error) throw error;
      setSubscribers((data ?? []) as SubscriberOption[]);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load connections');
      setSubscribers([]);
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedId(null);
    setSearchQuery('');
    setDebouncedQuery('');
    setSubscribers([]);
    void loadExcludeIds().catch((e: unknown) => {
      toast.error(e instanceof Error ? e.message : 'Failed to load recipient list');
    });
  }, [isOpen, loadExcludeIds]);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 450);
    return () => window.clearTimeout(t);
  }, [isOpen, searchQuery]);

  useEffect(() => {
    if (!isOpen) return;
    const background = subscribers.length > 0 || debouncedQuery.trim().length >= 2;
    void loadSubscribers(debouncedQuery, background);
  }, [isOpen, debouncedQuery, loadSubscribers]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !adding) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, adding]);

  const visible = useMemo(
    () => subscribers.filter((s) => !excludeIds.has(s.id)),
    [subscribers, excludeIds],
  );

  const handleAdd = async () => {
    if (!selectedId) {
      toast.error('Pick a connection first');
      return;
    }

    setAdding(true);
    try {
      await importNewsletterMissingRecipients(newsletterId, selectedId);

      toast.success('Connection added as pending send');
      onSuccess();
      onClose();
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'Add failed';
      toast.error(friendlyAddError(raw));
    } finally {
      setAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={() => {
        if (!adding) onClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-800 shadow-xl w-full max-w-lg h-[min(560px,85vh)] flex flex-col overflow-hidden min-h-0 h-[100dvh] max-h-[100dvh] rounded-none sm:h-auto sm:max-h-[min(90vh,900px)] sm:rounded-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-newsletter-recipient-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2
            id="add-newsletter-recipient-title"
            className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"
          >
            <UserPlus className="w-5 h-5 text-emerald-500" />
            Add recipient
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={adding}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700/80">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Add one connection to{' '}
            <span className="font-medium text-gray-900 dark:text-white">{newsletterName}</span> as a
            pending scheduled send.
          </p>
        </div>

        <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700/80">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email or name…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              autoFocus
            />
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Need a new contact?{' '}
            <Link to="/subscribers" className="text-emerald-600 dark:text-emerald-400 hover:underline">
              Open Connections
            </Link>{' '}
            to create one, then return here.
          </p>
        </div>

        <div className="relative px-6 py-4 flex-1 min-h-0 overflow-y-auto">
          {loading && visible.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
            </div>
          ) : visible.length === 0 ? (
            <div className="h-full flex items-center justify-center px-2">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                {searchQuery.trim().length >= 2
                  ? 'No matching connections found.'
                  : excludeIds.size > 0 && subscribers.length > 0
                    ? 'All matching connections are already on this recipient list.'
                    : 'No connections yet. Create one from the Connections page.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {visible.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(s.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedId === s.id
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900/30'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {displayLabel(s)}
                        </div>
                        {s.name && s.email ? (
                          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate">
                            {s.name}
                          </div>
                        ) : null}
                      </div>
                      <span
                        className="shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400"
                        title="Profile score (AI personalization richness)"
                      >
                        {profileScoreLabel(s)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {isSearching ? (
            <div
              className="absolute inset-0 flex items-center justify-center bg-white/70 dark:bg-gray-800/70 rounded-lg"
              aria-hidden
            >
              <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
            </div>
          ) : null}
        </div>

        <div className="shrink-0 flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={adding}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={adding || !selectedId}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 rounded-lg transition-colors"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {adding ? 'Adding…' : 'Add recipient'}
          </button>
        </div>
      </div>
    </div>
  );
}
